import React from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { GraduationCap, LayoutDashboard, Building2, School, BookOpen, ClipboardList, Newspaper, LogOut, ExternalLink, Loader2, UserSquare2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/colleges', label: 'Colleges', icon: Building2 },
  { to: '/admin/universities', label: 'Universities', icon: School },
  { to: '/admin/courses', label: 'Courses', icon: BookOpen },
  { to: '/admin/exams', label: 'Exams', icon: ClipboardList },
  { to: '/admin/blogs', label: 'Blogs', icon: Newspaper },
  { to: '/admin/authors', label: 'Authors', icon: UserSquare2 },
];

const AdminLayout = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!user) {
    navigate('/admin/login', { replace: true });
    return null;
  }

  if (!user.is_admin) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-4">
        <div className="bg-white rounded-2xl shadow p-8 max-w-md text-center">
          <h2 className="text-xl font-bold text-slate-900">Access denied</h2>
          <p className="text-slate-600 mt-2 text-sm">Your account ({user.email}) is not authorized to access the admin panel.</p>
          <Button onClick={logout} className="mt-5 bg-slate-900 hover:bg-slate-800 text-white">Sign out</Button>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-slate-50" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-slate-200 flex flex-col sticky top-0 h-screen">
        <Link to="/admin" className="flex items-center gap-2 px-5 py-5 border-b border-slate-800">
          <div className="w-9 h-9 rounded-lg bg-teal-600 grid place-items-center text-white">
            <GraduationCap className="w-5 h-5" />
          </div>
          <div>
            <div className="font-bold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Learn<span className="text-teal-400">dunia</span>
            </div>
            <div className="text-[10px] text-slate-500 tracking-widest uppercase">Admin CMS</div>
          </div>
        </Link>

        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-teal-600 text-white shadow' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-800 p-3">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-white hover:bg-slate-800">
            <ExternalLink className="w-3.5 h-3.5" /> View site
          </a>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="text-sm text-slate-500">Welcome back,</div>
          <div className="flex items-center gap-3">
            {user.picture && <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />}
            <div className="text-right leading-tight">
              <div className="text-sm font-semibold text-slate-900">{user.name}</div>
              <div className="text-[11px] text-slate-500">{user.email}</div>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </header>

        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
